/**
 * Parse a date string as LOCAL time (not UTC).
 * This prevents the off-by-one-day bug for date-only strings.
 *
 * The bug: `new Date('YYYY-MM-DD')` interprets date-only strings as UTC midnight.
 * When converted to local time for users west of UTC, this shows the wrong day.
 *
 * @example
 * parseLocalDate('2026-01-12') // Date representing Jan 12 at midnight LOCAL time
 */
export function parseLocalDate(dateStr: string | Date): Date {
  if (dateStr instanceof Date) return dateStr;

  // Date-only string (YYYY-MM-DD) - append T00:00:00 to force local interpretation
  if (!dateStr.includes('T') && !dateStr.includes(' ')) {
    return new Date(`${dateStr}T00:00:00`);
  }

  // ISO datetime with time component - parse normally
  return new Date(dateStr);
}

/**
 * Format a date string for document display (short format).
 * @example "Dec 25, 2025"
 */
export function formatDocumentDate(
  dateString: string | null | undefined,
): string {
  if (!dateString) return '';
  return parseLocalDate(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a date string for document display with time.
 * @example "Dec 25, 2025, 10:30 AM"
 */
export function formatDocumentDateTime(
  dateString: string | null | undefined,
): string {
  if (!dateString) return '';
  return parseLocalDate(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
